import React, { useMemo, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import { css } from '@emotion/native';
import { useRouter } from 'expo-router';
import { format } from 'date-fns';
import { GET_ADMIN_STYLISTS } from '@/shared/queries/admin';
import {
  AdminStylist,
  AdminStylistStatus,
  QueryResult,
} from '@/shared/types/generated';
import StaticInput from '@/shared/components/StaticInput';
import Checkbox from '@/shared/components/Checkbox';
import { green, red } from '@/shared/styles/colors';
import { ROUTES } from '@/shared/constants/routes';
import {
  ActionLink,
  Avatar,
  Cell,
  Column,
  EmailLink,
  EmptyState,
  ErrorState,
  FilterChips,
  Muted,
  Pill,
  Small,
  Spread,
  Stack,
  Strong,
  TableCard,
  TableHeader,
  TableRow,
} from './AdminUI';
import {
  cityName,
  inPersonSummary,
  lastInApp,
  plural,
  stylistState,
} from './format';
import StylistBookingEditor from './StylistBookingEditor';

type Filter = 'attention' | 'bookable' | 'unlisted' | 'all';

const FILTERS: Filter[] = ['attention', 'bookable', 'unlisted', 'all'];

const NEEDS_ATTENTION = [
  AdminStylistStatus.NoCalendar,
  AdminStylistStatus.NoUpcomingHours,
];

const matchesFilter = (stylist: AdminStylist, filter: Filter) => {
  switch (filter) {
    case 'attention':
      return NEEDS_ATTENTION.includes(stylist.status);
    case 'bookable':
      return stylist.status === AdminStylistStatus.Bookable;
    case 'unlisted':
      return stylist.status === AdminStylistStatus.NotListed;
    default:
      return true;
  }
};

const EMPTY: Record<Filter, { title: string; body: string }> = {
  attention: {
    title: 'All clear',
    body: 'Every listed stylist has a working calendar and open hours in the next 4 weeks.',
  },
  bookable: {
    title: 'Nobody is bookable',
    body: 'No listed stylist has hours clients can book right now. "Needs attention" shows why.',
  },
  unlisted: {
    title: 'Everyone is listed',
    body: 'Every stylist has finished Stripe setup.',
  },
  all: {
    title: 'No stylists yet',
    body: 'Stylists show up here once they sign up.',
  },
};

const COLUMNS: Column[] = [
  { key: 'stylist', label: 'Stylist', flex: 3 },
  { key: 'status', label: 'Status', flex: 3.2 },
  { key: 'hours', label: 'Open hours', flex: 1.8 },
  { key: 'sessions', label: 'Sessions', flex: 1.4 },
  { key: 'seen', label: 'Last in app', flex: 1.4 },
];

const hoursSummary = (stylist: AdminStylist) => {
  if (
    stylist.status === AdminStylistStatus.NotListed ||
    !stylist.nextDateWithHours
  ) {
    return null;
  }
  return {
    next: `Next ${format(new Date(`${stylist.nextDateWithHours}T12:00:00`), 'EEE, MMM d')}`,
    days: `${plural(stylist.upcomingDaysWithHours, 'day')} in 4 weeks`,
  };
};

const StylistsPanel = ({
  isDesktop,
  initialFilter,
}: {
  isDesktop: boolean;
  initialFilter?: string;
}) => {
  const router = useRouter();
  const [filter, setFilter] = useState<Filter>(
    FILTERS.find((f) => f === initialFilter) ?? 'attention',
  );
  const [search, setSearch] = useState('');
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [editingBookingFor, setEditingBookingFor] = useState<number | null>(
    null,
  );
  const { data, loading, error, refetch } = useQuery<
    QueryResult<'adminStylists'>
  >(GET_ADMIN_STYLISTS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const visible = useMemo(
    () =>
      (data?.adminStylists ?? []).filter(
        (stylist) => showTestAccounts || !stylist.isTestUser,
      ),
    [data, showTestAccounts],
  );
  const query = search.trim().toLowerCase();
  const matchesSearch = (stylist: AdminStylist) =>
    !query ||
    stylist.name.toLowerCase().includes(query) ||
    stylist.email.toLowerCase().includes(query);
  const stylists = visible.filter(
    (stylist) => matchesFilter(stylist, filter) && matchesSearch(stylist),
  );
  // No counts until loaded, so a failure never reads as 0
  const countOf = (f: Filter) =>
    data ? visible.filter((s) => matchesFilter(s, f)).length : null;

  const renderEmpty = () => {
    if (!query) {
      return <EmptyState {...EMPTY[filter]} />;
    }
    const elsewhere = visible.filter(matchesSearch).length;
    return (
      <EmptyState
        title="No matches"
        body={
          elsewhere
            ? `Nobody in this list matches "${search.trim()}", but ${plural(elsewhere, 'stylist')} under "All" ${elsewhere === 1 ? 'does' : 'do'}.`
            : `No stylist's name or email matches "${search.trim()}".`
        }
      />
    );
  };

  return (
    <View>
      <Spread>
        <FilterChips
          selected={filter}
          onSelect={setFilter}
          options={[
            {
              value: 'attention',
              label: 'Needs attention',
              count: countOf('attention'),
            },
            {
              value: 'bookable',
              label: 'Bookable',
              count: countOf('bookable'),
            },
            {
              value: 'unlisted',
              label: 'Not listed',
              count: countOf('unlisted'),
            },
            { value: 'all', label: 'All', count: countOf('all') },
          ]}
        />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
          <TouchableOpacity
            onPress={() => setShowTestAccounts((v) => !v)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: showTestAccounts }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
          >
            <Checkbox
              checked={showTestAccounts}
              onPress={() => setShowTestAccounts((v) => !v)}
            />
            <Muted>Show test accounts</Muted>
          </TouchableOpacity>
          <View style={{ width: isDesktop ? 242 : 180 }}>
            <StaticInput
              labelText="Search"
              showLabel
              placeholderText="Name or email"
              value={search}
              onChange={(event) => setSearch(event.nativeEvent.text)}
              customStyles={css({
                height: 42,
                fontSize: 13,
                color: green,
                width: '100%',
              })}
            />
          </View>
        </View>
      </Spread>

      {loading && !data ? (
        <ActivityIndicator
          size="large"
          color={green}
          style={{ marginTop: 80 }}
        />
      ) : error && !data ? (
        <ErrorState what="stylists" onRetry={() => refetch()} />
      ) : stylists.length === 0 ? (
        renderEmpty()
      ) : (
        <TableCard>
          {isDesktop ? <TableHeader columns={COLUMNS} /> : null}
          {stylists.map((stylist, index) => {
            const state = stylistState(stylist);
            const hours = hoursSummary(stylist);
            const city = cityName(stylist.city);
            const inPerson = inPersonSummary(stylist);
            const { shareCode } = stylist;
            return (
              <React.Fragment key={stylist.stylistId}>
                <TableRow isDesktop={isDesktop} isFirst={index === 0}>
                  <Cell flex={3} isDesktop={isDesktop}>
                    <View
                      style={{
                        flexDirection: 'row',
                        gap: 12,
                        alignItems: 'center',
                      }}
                    >
                      <Avatar
                        uri={stylist.profilePicUrl}
                        name={stylist.name}
                        size={36}
                      />
                      <Stack style={{ flexShrink: 1 }}>
                        <Strong numberOfLines={1}>{stylist.name}</Strong>
                        <EmailLink email={stylist.email} />
                        <View style={{ flexDirection: 'row', gap: 10 }}>
                          {city ? <Small>{city}</Small> : null}
                          <ActionLink
                            text="Booking"
                            accessibilityLabel={`${stylist.name}'s booking settings`}
                            onPress={() =>
                              setEditingBookingFor((id) =>
                                id === stylist.stylistId
                                  ? null
                                  : stylist.stylistId,
                              )
                            }
                          />
                          {shareCode ? (
                            <ActionLink
                              text="Profile"
                              accessibilityLabel={`${stylist.name}'s profile`}
                              onPress={() =>
                                router.push(
                                  ROUTES.STYLIST_PREVIEW(shareCode).path,
                                )
                              }
                            />
                          ) : null}
                        </View>
                        {inPerson ? (
                          <Small
                            style={
                              inPerson.isProblem ? { color: red } : undefined
                            }
                          >
                            {inPerson.text}
                          </Small>
                        ) : null}
                      </Stack>
                    </View>
                  </Cell>
                  <Cell flex={3.2} isDesktop={isDesktop}>
                    <Stack>
                      <Pill text={state.label} tone={state.tone} />
                      <Small numberOfLines={2}>{state.reason}</Small>
                    </Stack>
                  </Cell>
                  <Cell flex={1.8} isDesktop={isDesktop}>
                    {hours ? (
                      <Stack>
                        <Small style={{ color: green }}>{hours.next}</Small>
                        <Small>{hours.days}</Small>
                      </Stack>
                    ) : (
                      <Small>No open hours</Small>
                    )}
                  </Cell>
                  <Cell flex={1.4} isDesktop={isDesktop}>
                    <Stack>
                      <Small style={{ color: green }}>
                        {stylist.upcomingSessions} upcoming
                      </Small>
                      <Small>{stylist.completedSessions} completed</Small>
                    </Stack>
                  </Cell>
                  <Cell flex={1.4} isDesktop={isDesktop}>
                    <Stack>
                      <Small>{lastInApp(stylist.lastSeenAt)}</Small>
                      {stylist.instantStylingActive ? (
                        <Small style={{ color: green }}>Instant on</Small>
                      ) : null}
                    </Stack>
                  </Cell>
                </TableRow>
                {editingBookingFor === stylist.stylistId ? (
                  <StylistBookingEditor
                    stylist={stylist}
                    isDesktop={isDesktop}
                    // A slow save mustn't close another stylist's editor
                    onClose={() =>
                      setEditingBookingFor((id) =>
                        id === stylist.stylistId ? null : id,
                      )
                    }
                    onSaved={refetch}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </TableCard>
      )}
    </View>
  );
};

export default StylistsPanel;
